import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import getApiBaseUrl from '../utils/getApiBaseUrl';

// Word pairs für Access Keys
const ADJECTIVES = ['spoon', 'happy', 'quick', 'bright', 'swift', 'brave', 'calm', 'eager', 'fresh', 'gentle', 'golden', 'icy', 'jolly', 'keen', 'lucky', 'mighty', 'nimble', 'orange', 'purple', 'quiet', 'royal', 'silver', 'tiny', 'urban', 'vivid', 'warm', 'yellow', 'zesty'];
const NOUNS = ['guitar', 'piano', 'drums', 'bird', 'cat', 'dog', 'fish', 'lion', 'tiger', 'eagle', 'snake', 'bear', 'wolf', 'tree', 'river', 'mountain', 'ocean', 'desert', 'forest', 'island', 'cloud', 'storm', 'sunrise', 'sunset', 'moon', 'star', 'planet', 'comet'];

// Logout-Modal mit Word-Combo Access Key
const LogoutModal = ({ accessKey, onConfirm, onCancel }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const [showKey, setShowKey] = useState(false);

    React.useEffect(() => {
        if (accessKey) {
            const showTimer = setTimeout(() => setShowKey(true), 2000);
            const hideTimer = setTimeout(() => setShowKey(false), 8000);
            return () => {
                clearTimeout(showTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [accessKey]);

    const handleCopy = () => {
        if (!accessKey) return;
        navigator.clipboard.writeText(accessKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <AnimatePresence>
            {true && (
                <motion.div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 1200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onCancel}
                >
                    <motion.div
                        style={{
                            background: '#ffffff',
                            borderRadius: '24px',
                            padding: '28px',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            position: 'relative'
                        }}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={onCancel}
                            style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#94a3b8'
                            }}
                        >
                            ✕
                        </button>

                        {/* Title */}
                        <h2 style={{
                            fontSize: '1.5em',
                            fontWeight: '700',
                            textAlign: 'center',
                            marginBottom: '12px',
                            color: '#1a202c',
                            fontFamily: 'var(--font-display)'
                        }}>
                            {t('save_access_title', 'Save Your Access Key')}
                        </h2>

                        {/* Description */}
                        <p style={{
                            textAlign: 'center',
                            fontSize: '0.9em',
                            color: '#4a5568',
                            marginBottom: '24px'
                        }}>
                            {t('save_access_prompt', 'Scan or copy to restore your identity on other devices. Don\'t lose it!')}
                        </p>

                        {/* Full Screen Display */}
                        <AnimatePresence>
                            {showKey && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    style={{
                                        position: 'fixed',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 1300,
                                        background: 'rgba(0, 0, 0, 0.95)',
                                        borderRadius: '20px',
                                        padding: '30px',
                                        border: '3px solid #6366f1',
                                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                                        maxWidth: '90%',
                                        width: '600px'
                                    }}
                                >
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <div style={{
                                            fontSize: '0.9em',
                                            color: '#ffffff',
                                            marginBottom: '10px',
                                            fontWeight: '500'
                                        }}>
                                            ⚠️ {t('save_access_title', 'Save Your Access Key')}
                                        </div>
                                    </div>
                                    
                                    <p style={{
                                        fontSize: '2.5em',
                                        fontWeight: '800',
                                        color: '#ffffff',
                                        margin: '0 0 25px 0',
                                        fontFamily: 'monospace',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.15em',
                                        lineHeight: '1.2',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        wordBreak: 'break-word'
                                    }}>
                                        {accessKey}
                                    </p>
                                    
                                    <div style={{
                                        textAlign: 'center',
                                        marginTop: '25px'
                                    }}>
                                        <div style={{
                                            fontSize: '1.1em',
                                            color: '#ffffff',
                                            marginBottom: '15px',
                                            fontWeight: '600',
                                            lineHeight: '1.4'
                                        }}>
                                            {t('remember_this', 'Remember this!')}
                                        </div>
                                        <div style={{
                                            fontSize: '0.9em',
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            fontStyle: 'italic'
                                        }}>
                                            {t('access_key_reminder', 'These 2 words are your only way back in.')}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Access Key Display */}
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(99, 102, 241, 0.05))',
                            borderRadius: '16px',
                            padding: '30px 20px',
                            marginBottom: '20px',
                            border: '2px solid rgba(99, 102, 241, 0.2)',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                fontSize: '3em',
                                fontWeight: '700',
                                color: '#6366f1',
                                letterSpacing: '0.15em',
                                margin: '0',
                                fontFamily: 'monospace',
                                textTransform: 'uppercase',
                                wordBreak: 'break-word'
                            }}>
                                {accessKey}
                            </p>
                        </div>

                        {/* Copy Button */}
                        <motion.button
                            onClick={handleCopy}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                fontWeight: '700',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#ffffff',
                                marginBottom: '10px',
                                background: copied ? '#34d399' : '#6366f1',
                                transition: 'all 0.2s'
                            }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {copied ? '✓ ' + t('copied', 'Copied!') : t('copy_to_clipboard', 'Copy to Clipboard')}
                        </motion.button>

                        {/* Confirm/Cancel Buttons */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <motion.button
                                onClick={onCancel}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    border: '1px solid rgba(99, 102, 241, 0.2)',
                                    cursor: 'pointer',
                                    color: '#1a202c',
                                    background: '#f1f5f9',
                                    transition: 'all 0.2s'
                                }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {t('cancel_button', 'Cancel')}
                            </motion.button>
                            <motion.button
                                onClick={onConfirm}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#ffffff',
                                    background: '#ef4444',
                                    transition: 'all 0.2s'
                                }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {t('logout_button', 'Logout')}
                            </motion.button>
                        </div>

                        {/* Info Text */}
                        <p style={{
                            textAlign: 'center',
                            fontSize: '0.75em',
                            color: '#94a3b8',
                            marginTop: '16px'
                        }}>
                            {t('access_key_security', 'Keep your key safe - it grants access to your profile!')}
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [accessKey, setAccessKey] = useState(null);
    const navigate = useNavigate();
    const csrfTokenEnsurer = useRef(() => Promise.resolve());
    const memoryTokenStore = useRef(null); // Fallback für Inkognito-Modus

    // Helper: Token speichern (localStorage + Memory)
    const saveToken = (token) => {
        memoryTokenStore.current = token; // Immer speichern!
        try {
            localStorage.setItem('jwt_token', token);
        } catch (e) {
            console.warn('[Auth] localStorage unavailable (Incognito?), using memory storage');
        }
    };

    // Helper: Token löschen (Memory + localStorage)
    const clearToken = () => {
        memoryTokenStore.current = null;
        try {
            localStorage.removeItem('jwt_token');
        } catch (e) {
            // localStorage might not be available
        }
    };

    // Helper: Token holen (Memory + localStorage)
    const getToken = () => {
        if (memoryTokenStore.current) return memoryTokenStore.current;
        try {
            return localStorage.getItem('jwt_token');
        } catch (e) {
            // localStorage might not be available
            return null;
        }
    };

    // Stabile API-Instanz mit useMemo
    const api = useMemo(() => {
        const apiBaseUrl = getApiBaseUrl();
        const instance = axios.create({
            baseURL: apiBaseUrl || undefined,
            withCredentials: false  // No need for credentials since we use Authorization header
        });

        // CSRF Token Interceptor (Auto-add für POST/PUT/DELETE)
        let csrfToken = null;
        let csrfTokenPromise = null;

        const fetchCSRFToken = async () => {
            if (csrfToken) return csrfToken;
            if (csrfTokenPromise) return csrfTokenPromise;
            csrfTokenPromise = instance.get('/api/csrf-token')
                .then(res => {
                    csrfToken = res.data.token;
                    csrfTokenPromise = null;
                    return csrfToken;
                })
                .catch(err => {
                    csrfTokenPromise = null;
                    console.error('Failed to fetch CSRF token', err);
                    throw err;
                });
            return csrfTokenPromise;
        };
        csrfTokenEnsurer.current = fetchCSRFToken;
        fetchCSRFToken().catch(() => {});

        // Add CSRF token and Authorization header to requests
        instance.interceptors.request.use((config) => {
            if (['post', 'put', 'delete', 'patch'].includes(config.method) && csrfToken) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
            // Add JWT from memory or localStorage (works in Incognito mode) - für alle Requests!
            const token = getToken();
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            } else {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[Auth] no JWT token available for request to', config.url);
                }
            }
            return config;
        });

        return instance;
    }, []);

    const ensureCsrfToken = () => csrfTokenEnsurer.current();

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const res = await api.get('/api/auth/check');
                if (res.data.isAuthenticated) {
                    setUser(res.data.user);
                    setIsAuthenticated(true);
                }
            } catch (error) { /* Silent fail */ }
            finally { setIsLoading(false); }
        };
        checkAuthStatus();
    }, []);

    const startSession = async (formData) => {
        try {
            const res = await api.post('/api/auth/start', formData);
            if (res.data.user && res.data.token) {
                // Save JWT (works in localStorage or memory for Incognito)
                saveToken(res.data.token);
                setUser(res.data.user);
                setIsAuthenticated(true);
                navigate('/', { replace: true });
                return res.data;
            }
        } catch (error) {
            // prefer detailed message, fall back to network error text
            const msg = error.response?.data?.message || error.message || 'Start failed';
            throw msg;
        }
    };

    const openLogoutModal = async () => {
        let key = null;
        
        try {
            const res = await api.get('/api/auth/check');
            key = res.data.accessKey;
        } catch (error) {
            // Fallback: generate key from user data if available
            if (user && user.id) {
                key = generateAccessKeyFromId(user.id);
            } else {
                // Last resort: use the actual token (suboptimal but works)
                key = '...';
            }
        }
        
        setAccessKey(key);
        setShowLogoutModal(true);
    };

    const generateAccessKeyFromId = (userId) => {
        // Generiere einen deterministischen Access Key aus der User ID
        let hash = userId;
        for (let i = 0; i < userId.toString().length; i++) {
            const char = userId.toString().charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        const absHash = Math.abs(hash);
        const adjIdx = absHash % ADJECTIVES.length;
        const nounIdx = (Math.floor(absHash / ADJECTIVES.length)) % NOUNS.length;
        return `${ADJECTIVES[adjIdx]}-${NOUNS[nounIdx]}`;
    };

    const confirmLogout = async () => {
        try {
            await api.post('/api/auth/logout');
            clearToken();
            setUser(null);
            setIsAuthenticated(false);
            setShowLogoutModal(false);
            navigate('/start', { replace: true });
        } catch (error) { console.error('Logout failed:', error); }
    };

    const loginWithKey = async (key) => {
        try {
            const res = await api.post('/api/auth/login-key', { key });
            if (res.data.token) {
                saveToken(res.data.token);
                // Update axios instance with new token
                const checkRes = await api.get('/api/auth/check');
                if (checkRes.data.isAuthenticated) {
                    setUser(checkRes.data.user);
                    setIsAuthenticated(true);
                    navigate('/', { replace: true });
                }
            }
        } catch (e) {
            throw new Error('Invalid or expired key.');
        }
    };

    const updateUser = async (data) => {
        await ensureCsrfToken();
        try {
            await api.put('/api/auth/user', data);
            const res = await api.get('/api/auth/check');
            if (res.data.isAuthenticated) {
                setUser(res.data.user);
            }
        } catch (error) {
            throw error.response?.data?.message || 'Update failed.';
        }
    };

    const value = { isAuthenticated, user, isLoading, startSession, logout: openLogoutModal, loginWithKey, updateUser, api, ensureCsrfToken, getToken };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <AnimatePresence>
                {showLogoutModal && (
                    <LogoutModal 
                        accessKey={accessKey}
                        onConfirm={confirmLogout}
                        onCancel={() => setShowLogoutModal(false)}
                    />
                )}
            </AnimatePresence>
        </AuthContext.Provider>
    );
};
